// FILE: date-time-picker.tsx
// Purpose: Pick a day and a time inside the app, instead of handing the job to the browser's
//          native date and time fields.
// Layer: View
// Exports: DateTimePicker

import { ChevronLeftIcon, ChevronRightIcon } from "~/lib/icons";
import { useMemo, useState } from "react";

import {
  HOUR_OPTIONS,
  MINUTE_OPTIONS,
  WEEKDAY_LABELS,
  buildMonthGrid,
  formatMonthLabel,
  fromDateInputValue,
  shiftMonth,
  toDateInputValue,
} from "~/lib/calendarGrid";
import { cn } from "~/lib/utils";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "./select";

interface DateTimePickerProps {
  /** `YYYY-MM-DD`, the same shape `<input type="date">` produced. */
  date: string;
  /** `HH:MM` in 24-hour form. */
  time: string;
  disabled?: boolean;
  onDateChange: (date: string) => void;
  onTimeChange: (time: string) => void;
  /** Days before this one are not selectable. Local midnight, not an instant. */
  minDate?: Date;
}

/**
 * A calendar and a clock the app draws itself.
 *
 * `<input type="date">` renders whatever the operating system feels like: the
 * field reads `dd/mm/aaaa` on one machine and `mm/dd/yyyy` on the next, the
 * popup is in the OS language rather than the app's, and on some platforms
 * there is no popup at all. For a control whose whole job is answering "when
 * does this come back?", that is the one thing it must not be vague about.
 *
 * So the grid, the month names and the hour list are all ours. Nothing here
 * reads a locale from the environment — the weekday letters are a constant and
 * the month heading is asked for in `es` explicitly.
 */
export function DateTimePicker({
  date,
  time,
  disabled = false,
  onDateChange,
  onTimeChange,
  minDate,
}: DateTimePickerProps) {
  const selectedDate = fromDateInputValue(date);
  // The visible month follows the selection until somebody pages away, and then
  // stops following it — otherwise choosing a day would be the last thing you
  // could do before the calendar snapped back.
  const [visibleMonth, setVisibleMonth] = useState<Date>(() => selectedDate ?? new Date());

  const grid = useMemo(() => buildMonthGrid(visibleMonth), [visibleMonth]);
  const minValue = minDate ? toDateInputValue(minDate) : null;
  const todayValue = toDateInputValue(new Date());

  const [hour = "09", minute = "00"] = time.split(":");

  const commitTime = (nextHour: string, nextMinute: string) => {
    onTimeChange(`${nextHour}:${nextMinute}`);
  };

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          disabled={disabled}
          aria-label="Mes anterior"
          onClick={() => setVisibleMonth((current) => shiftMonth(current, -1))}
          className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-55"
        >
          <ChevronLeftIcon className="size-3.5" />
        </button>
        <span
          aria-live="polite"
          className="text-[length:var(--app-font-size-ui,12px)] font-medium text-foreground"
        >
          {formatMonthLabel(visibleMonth)}
        </span>
        <button
          type="button"
          disabled={disabled}
          aria-label="Mes siguiente"
          onClick={() => setVisibleMonth((current) => shiftMonth(current, 1))}
          className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-55"
        >
          <ChevronRightIcon className="size-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {WEEKDAY_LABELS.map((label, index) => (
          <span
            // The letters repeat (M for martes and miércoles), so the position
            // is the identity here, not the text.
            key={`${label}-${index}`}
            aria-hidden="true"
            className="pb-1 text-center text-[length:var(--app-font-size-ui-xs,10px)] text-muted-foreground/60"
          >
            {label}
          </span>
        ))}
        {grid.map((day) => {
          const isSelected = day.value === date;
          const isToday = day.value === todayValue;
          const isDisabled = disabled || (minValue !== null && day.value < minValue);
          return (
            <button
              key={day.value}
              type="button"
              disabled={isDisabled}
              aria-pressed={isSelected}
              onClick={() => {
                onDateChange(day.value);
                // Clicking a borrowed day from the next month is a request to
                // go there, not just to pick that date.
                if (!day.inCurrentMonth) {
                  const picked = fromDateInputValue(day.value);
                  if (picked) setVisibleMonth(picked);
                }
              }}
              className={cn(
                "inline-flex h-7 items-center justify-center rounded-md text-[length:var(--app-font-size-ui,12px)] transition-colors",
                day.inCurrentMonth ? "text-foreground/85" : "text-muted-foreground/40",
                isSelected
                  ? "bg-primary text-primary-foreground hover:bg-primary"
                  : "hover:bg-accent/60",
                // A ring rather than a fill, so today never competes with the
                // day actually chosen.
                !isSelected && isToday ? "ring-1 ring-inset ring-border" : undefined,
                isDisabled ? "pointer-events-none opacity-30" : undefined,
              )}
            >
              {day.dayOfMonth}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <span className="text-[length:var(--app-font-size-ui,12px)] text-muted-foreground">
          Hora
        </span>
        <Select
          value={hour}
          disabled={disabled}
          onValueChange={(value) => {
            if (typeof value !== "string") return;
            commitTime(value, minute);
          }}
        >
          <SelectTrigger size="sm" className="w-16 min-w-0" aria-label="Hora">
            <SelectValue>{hour}</SelectValue>
          </SelectTrigger>
          <SelectPopup align="start" alignItemWithTrigger={false}>
            {HOUR_OPTIONS.map((option) => (
              <SelectItem key={option} hideIndicator value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectPopup>
        </Select>
        <span aria-hidden="true" className="text-muted-foreground">
          :
        </span>
        <Select
          value={minute}
          disabled={disabled}
          onValueChange={(value) => {
            if (typeof value !== "string") return;
            commitTime(hour, value);
          }}
        >
          <SelectTrigger size="sm" className="w-16 min-w-0" aria-label="Minutos">
            <SelectValue>{minute}</SelectValue>
          </SelectTrigger>
          <SelectPopup align="start" alignItemWithTrigger={false}>
            {MINUTE_OPTIONS.map((option) => (
              <SelectItem key={option} hideIndicator value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectPopup>
        </Select>
      </div>
    </div>
  );
}
